import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";

interface GoogleMomentNotification {
  isNotDisplayed?: () => boolean;
  isSkippedMoment?: () => boolean;
  isDismissedMoment?: () => boolean;
  getNotDisplayedReason?: () => string;
  getSkippedReason?: () => string;
  getDismissedReason?: () => string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
            cancel_on_tap_outside?: boolean;
            // Chrome is migrating One Tap to the FedCM API. It's flaky on
            // localhost/self-signed dev certs (NetworkError: "Provider's FedCM
            // config file not listed in its well-known file") — opt out since
            // we only need the plain ID token, none of FedCM's UX/privacy bits.
            use_fedcm_for_prompt?: boolean;
          }) => void;
          prompt: (momentListener?: (notification: GoogleMomentNotification) => void) => void;
          renderButton: (parent: HTMLElement, options: { type: "standard" | "icon" }) => void;
        };
      };
    };
  }
}

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

/**
 * Thin wrapper around Google Identity Services' JS API. Loads the GSI script on
 * demand and resolves with the raw ID token so it can be posted straight through
 * to the backend's "google" grant type — see GOOGLE_SSO_PLAN.md. Deliberately
 * doesn't go through the SSO / KeyConnectorService login flow; this is a self-contained path.
 *
 * One Tap (`prompt()`) is built for automatic/ambient display and Chrome applies
 * a cooldown ("suppressed_by_user") once a user has dismissed it before — that
 * makes it unreliable as the *only* path behind an explicit "Sign in with
 * Google" button. Google's own guidance for that case is to fall back to their
 * rendered button. We keep our own pill-styled button as the only UI the user
 * sees, and pre-render Google's real button invisibly as a fallback trigger
 * target so a suppressed prompt still gets the user to a working sign-in flow
 * from the same click.
 *
 * None of that works inside the Capacitor app: the web view can't open GSI's
 * popup, `capacitor://` isn't an origin a web client ID accepts, and Google
 * blocks OAuth in embedded web views anyway. On native platforms we go through
 * Google's native sign-in SDK via @capgo/capacitor-social-login instead, which
 * hands back the same kind of ID token for the same backend grant.
 */
@Injectable({
  providedIn: "root",
})
export class GoogleIdentityService {
  private scriptPromise: Promise<void> | null = null;
  private initialized = false;
  private hiddenButtonEl: HTMLElement | null = null;
  private pendingResolve: ((idToken: string) => void) | null = null;
  private pendingReject: ((e: Error) => void) | null = null;
  private pendingFocusHandler: (() => void) | null = null;
  private pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private nativeInitPromise: Promise<void> | null = null;

  /**
   * Triggers Google sign-in and resolves with the signed Google ID token.
   * Tries One Tap first; if that's suppressed/unavailable, falls back to a
   * synthetic click on an invisibly pre-rendered Google button. Rejects if the
   * client ID isn't configured, the script fails to load, or the user actively
   * cancels.
   */
  async getIdToken(): Promise<string> {
    const clientId = (process.env.GOOGLE_CLIENT_ID as unknown as string) || "";
    if (!clientId) {
      throw new Error("Google sign-in is not configured.");
    }

    if (Capacitor.isNativePlatform()) {
      return this.getNativeIdToken(clientId);
    }

    await this.ensureInitialized(clientId);

    return new Promise<string>((resolve, reject) => {
      this.pendingResolve = resolve;
      this.pendingReject = reject;

      // Safety net: the fallback (Google's own rendered button) can open a real
      // popup window we don't control. If the user closes it without finishing
      // sign-in, no GIS callback ever fires and this promise would hang
      // forever — stranding the button in a loading state. Watch for the tab
      // regaining focus (popup closed / user tabbed back) and, after a short
      // grace period for a still-in-flight callback, treat silence as a
      // cancel. A hard timeout backstops the case where focus never fires.
      this.pendingFocusHandler = () => {
        setTimeout(() => {
          this.settle(null, new Error("Google sign-in was cancelled."));
        }, 1500);
      };
      window.addEventListener("focus", this.pendingFocusHandler, { once: true });

      this.pendingTimeoutId = setTimeout(() => {
        this.settle(null, new Error("Google sign-in timed out."));
      }, 90000);

      window.google!.accounts.id.prompt((notification) => {
        if (notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.()) {
          if (!this.clickHiddenButton()) {
            const reason =
              notification.getNotDisplayedReason?.() ??
              notification.getSkippedReason?.() ??
              "unknown reason";
            this.settle(null, new Error(`Google sign-in is unavailable right now (${reason}).`));
          }
        } else if (notification?.isDismissedMoment?.()) {
          const reason = notification.getDismissedReason?.();
          // "credential_returned" means the callback already resolved this
          // promise — anything else is the user actively closing the prompt.
          if (reason !== "credential_returned") {
            this.settle(
              null,
              new Error(`Google sign-in was cancelled (${reason ?? "dismissed"}).`),
            );
          }
        }
      });
    });
  }

  private async getNativeIdToken(webClientId: string): Promise<string> {
    await this.ensureNativeInitialized(webClientId);

    // No custom scopes: the plugin's defaults (email, profile) are all the
    // backend's "google" grant needs, and custom scopes on Android require
    // patching MainActivity in the native shell.
    const response = await SocialLogin.login({
      provider: "google",
      options: { forcePrompt: true },
    });
    if (
      response.provider !== "google" ||
      response.result.responseType !== "online" ||
      !response.result.idToken
    ) {
      throw new Error("Google sign-in did not return a credential.");
    }
    return response.result.idToken;
  }

  private ensureNativeInitialized(webClientId: string): Promise<void> {
    if (this.nativeInitPromise) {
      return this.nativeInitPromise;
    }

    const iosClientId = (process.env.GOOGLE_IOS_CLIENT_ID as unknown as string) || "";
    if (Capacitor.getPlatform() === "ios" && !iosClientId) {
      return Promise.reject(new Error("Google sign-in is not configured for iOS."));
    }

    this.nativeInitPromise = SocialLogin.initialize({
      google: {
        webClientId,
        iOSClientId: iosClientId || undefined,
        // Passing the web client ID as the server client ID is meant to make
        // the ID token's `aud` the web client ID, so gloss-back's verifier keeps
        // working unchanged. Confirm on a real device — if iOS still issues
        // tokens with `aud` = iOS client ID, the backend has to accept both.
        iOSServerClientId: webClientId,
        mode: "online",
      },
    }).catch((e) => {
      // Same as loadScript: don't cache a failed init.
      this.nativeInitPromise = null;
      throw e;
    });

    return this.nativeInitPromise;
  }

  private settle(idToken: string | null, error: Error | null) {
    // Already settled (e.g. the focus-grace-period timer firing after the
    // real callback already resolved) — ignore, don't fire twice.
    if (this.pendingResolve == null && this.pendingReject == null) {
      return;
    }

    if (this.pendingFocusHandler != null) {
      window.removeEventListener("focus", this.pendingFocusHandler);
      this.pendingFocusHandler = null;
    }
    if (this.pendingTimeoutId != null) {
      clearTimeout(this.pendingTimeoutId);
      this.pendingTimeoutId = null;
    }

    if (idToken != null) {
      this.pendingResolve?.(idToken);
    } else if (error != null) {
      this.pendingReject?.(error);
    }
    this.pendingResolve = null;
    this.pendingReject = null;
  }

  private async ensureInitialized(clientId: string): Promise<void> {
    await this.loadScript();
    if (this.initialized) {
      return;
    }

    window.google!.accounts.id.initialize({
      client_id: clientId,
      cancel_on_tap_outside: true,
      use_fedcm_for_prompt: false,
      callback: (response) => {
        if (response?.credential) {
          this.settle(response.credential, null);
        } else {
          this.settle(null, new Error("Google sign-in did not return a credential."));
        }
      },
    });

    this.renderHiddenButton();
    this.initialized = true;
  }

  private renderHiddenButton(): void {
    const container = document.createElement("div");
    // Off-screen, not display:none — Google's script needs real layout to
    // render into. This is never meant to be seen; our own gloss-button is the
    // only UI the user interacts with, this just exists as a fallback trigger
    // target for when One Tap itself won't display.
    container.style.position = "fixed";
    container.style.top = "-9999px";
    container.style.left = "-9999px";
    document.body.appendChild(container);

    window.google!.accounts.id.renderButton(container, { type: "standard" });
    this.hiddenButtonEl = container;
  }

  private clickHiddenButton(): boolean {
    const target = this.hiddenButtonEl?.querySelector<HTMLElement>('div[role="button"]');
    if (!target) {
      return false;
    }
    target.click();
    return true;
  }

  private loadScript(): Promise<void> {
    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = GSI_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        script.remove();
        reject(new Error("Failed to load Google Identity Services."));
      };
      document.head.appendChild(script);
    }).catch((e) => {
      // Don't cache a failed load — a transient network blip shouldn't
      // permanently disable the button for the rest of the session.
      this.scriptPromise = null;
      throw e;
    });

    return this.scriptPromise;
  }
}
